import React, { useState } from 'react';

import { IconButton } from '@rbilabs/universal-components';

import ConfirmDialog from 'components/confirm-dialog';
import { withFavoriteActionGate } from 'components/favorite-action-gate';
import { FavoriteIcon } from 'components/favorite-icon';
import { SaveFavoriteOrder } from 'components/save-favorite-order';
import { toaster } from 'components/toaster/toaster';
import {
  GetUserFavoritesRefsDocument,
  GetUserMinimalFavoritesDocument,
  useCreateMinimalFavoriteMutation,
  useDeleteMinimalFavoriteMutation,
  useGetUserMinimalFavoritesQuery,
} from 'generated/graphql-gateway';
import { useFavoritesContext } from 'state/favorites';
import { t } from 'state/intl/translations';

import { MinimalRecentOrderData } from './types';

const MinimalRecentOrderFavoriteButtonContent = ({ order }: { order: MinimalRecentOrderData }) => {
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [removeModalOpen, setRemoveModalOpen] = useState(false);
  const { setShouldRefetchFavorites } = useFavoritesContext();
  const { data } = useGetUserMinimalFavoritesQuery({
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first',
  });
  const [deleteMinimalFavorite, { loading: deleteLoading }] = useDeleteMinimalFavoriteMutation();
  const [createMinimalFavorite, { loading: createLoading }] = useCreateMinimalFavoriteMutation();

  const favoriteMatch = data?.userMinimalFavorites?.favorites?.find(
    favorite => favorite?.ref === order.rbiOrderId
  );
  const isFavorite = !!favoriteMatch;
  const loading = createLoading || deleteLoading;
  const accessibilityLabelFavorites = isFavorite ? t('removeFavorite') : t('addFavorite');

  const handleSaveFavoriteOrder = async (name: string) => {
    return createMinimalFavorite({
      variables: {
        input: {
          entries: order.menuSelections,
          name,
          ref: order.rbiOrderId,
        },
      },
      awaitRefetchQueries: true,
      refetchQueries: [
        { query: GetUserFavoritesRefsDocument },
        { query: GetUserMinimalFavoritesDocument },
      ],
    });
  };

  const handleRemoveFavoriteOrder = async () => {
    if (!favoriteMatch?._id) {
      setRemoveModalOpen(false);
      return;
    }

    try {
      await deleteMinimalFavorite({
        variables: {
          favoriteId: favoriteMatch._id,
        },
        awaitRefetchQueries: true,
        refetchQueries: [
          { query: GetUserFavoritesRefsDocument },
          { query: GetUserMinimalFavoritesDocument },
        ],
      });
      setShouldRefetchFavorites(true);
      toaster.positive(t('removeFavoriteSucess'));
    } finally {
      setRemoveModalOpen(false);
    }
  };

  return (
    <>
      <IconButton
        variant="ghost"
        onPress={() => {
          if (isFavorite) {
            setRemoveModalOpen(true);
          } else {
            setSaveModalOpen(true);
          }
        }}
        testID="favorite-order-icon"
        disabled={loading}
        accessibilityLabel={accessibilityLabelFavorites}
        icon={<FavoriteIcon isFavorite={isFavorite} disabled={loading} loading={loading} />}
      />

      {removeModalOpen && (
        <ConfirmDialog
          showDialog
          heading={t('removeFavorite')}
          body={t('removeFavoriteConfirm')}
          cancelLabel={t('cancel')}
          confirmLabel={t('remove')}
          onCancel={() => setRemoveModalOpen(false)}
          onConfirm={handleRemoveFavoriteOrder}
          modalAppearanceEventMessage="Delete Favorite Order"
        />
      )}

      {saveModalOpen && (
        <SaveFavoriteOrder
          showDialog={saveModalOpen}
          onSave={name => {
            handleSaveFavoriteOrder(name.trim())
              .then(() => {
                setShouldRefetchFavorites(true);
                toaster.positive(t('saveFavoriteOrderSucess'));
              })
              .catch(() => {
                toaster.negative(t('itemFavoritedError'));
              });
            setSaveModalOpen(false);
          }}
          onDismiss={() => setSaveModalOpen(false)}
        />
      )}
    </>
  );
};

export const MinimalRecentOrderFavoriteButton = withFavoriteActionGate(
  MinimalRecentOrderFavoriteButtonContent
);
